import { cn } from '@/shared/lib/utils';
import { _t } from '@/i18n/t';
import type { Chapter } from './ChapterRow';
import SiteScanPanel, { type SiteScanData } from './SiteScanPanel';
import GoogleChecklistPanel, { type SerpSite } from './GoogleChecklistPanel';
import AssistantsPanel, { type AssistantsData } from './AssistantsPanel';
import CustomersPanel, { type CustomerPersonaCard } from './CustomersPanel';
import type { AudienceFact } from '../hooks/scanEventRouter';
import ScanBeatHeading from './ScanBeatHeading';
import { foldProgressLines } from '../utils/scanProgress';

interface Props {
  chapter: Chapter;
  done: boolean;
  liveMessages: string[];
  replaying?: boolean;
  settled?: boolean;
  slowHint?: string;
  siteScanData?: SiteScanData;
  googleQueries?: string[];
  serpSite?: SerpSite;
  assistantsData?: AssistantsData;
  /** The search pass has settled, so its panel is replaying rather than following. */
  googleComplete?: boolean;
  onGooglePlaybackComplete?: () => void;
  sitePagesFinal?: boolean;
  onSitePlaybackComplete?: () => void;
  /** The assistants pass has settled, so its panel is replaying rather than following. */
  assistantsComplete?: boolean;
  onAssistantsPlaybackComplete?: () => void;
  customersData?: { personas: CustomerPersonaCard[]; expectedTotal: number; progressKey?: string; audienceFacts?: AudienceFact[] };
}

export default function ScanFocalChapter({
  chapter, done, liveMessages, replaying = false, settled = false, slowHint, siteScanData,
  googleQueries, serpSite, assistantsData, customersData,
  googleComplete, onGooglePlaybackComplete, sitePagesFinal, onSitePlaybackComplete, assistantsComplete, onAssistantsPlaybackComplete,
}: Props) {
  const isBasicsChapter = chapter.key === 'site' && !done;

  return (
    <div>
      {(!isBasicsChapter || replaying) && <div className="flex items-center gap-3 mb-7 flex-wrap">
        <ScanBeatHeading text={done ? _t('onboarding.crawl.compileHead') : chapter.label} />
      </div>}

      {done ? (
        <p className="text-muted-foreground" style={{ fontSize: 16, lineHeight: 1.55 }}>{chapter.settled}</p>
      ) : (
        <>
          {!replaying && isBasicsChapter && siteScanData ? (
            <section className="min-w-0">
              <div className="mb-9">
                <ScanBeatHeading text={chapter.label} />
              </div>
              <SiteScanPanel data={siteScanData} pagesFinal={sitePagesFinal} onPlaybackComplete={onSitePlaybackComplete} />
            </section>
          ) : !replaying && siteScanData ? <SiteScanPanel data={siteScanData} pagesFinal={sitePagesFinal} onPlaybackComplete={onSitePlaybackComplete} /> : null}
          {!replaying && googleQueries !== undefined && (
            <GoogleChecklistPanel
              queries={googleQueries}
              site={serpSite}
              complete={googleComplete}
              onPlaybackComplete={onGooglePlaybackComplete}
            />
          )}
          {!replaying && assistantsData && (
            <AssistantsPanel
              data={assistantsData}
              complete={assistantsComplete}
              onPlaybackComplete={onAssistantsPlaybackComplete}
            />
          )}
          {!replaying && customersData && (
            <CustomersPanel
              personas={customersData.personas}
              expectedTotal={customersData.expectedTotal}
              progressKey={customersData.progressKey}
              liveMessages={liveMessages}
              audienceFacts={customersData.audienceFacts}
            />
          )}

          {(replaying || (!siteScanData && googleQueries === undefined && !assistantsData && !customersData)) && (
            <div className="flex flex-col gap-2" data-testid="onboarding-scan-chapter-messages">
              {liveMessages.length > 0 ? (
                foldProgressLines(liveMessages).slice(-4).map((row, i, arr) => (
                  <div
                    key={row.text}
                    className={cn(
                      'transition-all duration-500',
                      i < arr.length - 1 ? 'text-muted-foreground opacity-40' : 'text-foreground font-medium',
                    )}
                    style={{ fontSize: '1.1rem', lineHeight: 1.5, letterSpacing: '-0.005em' }}
                  >
                    {row.text}
                    {row.elapsed && <span className="ml-2 tabular-nums text-muted-foreground">{row.elapsed}</span>}
                  </div>
                ))
              ) : (
                <div className="text-muted-foreground" style={{ fontSize: '1.1rem' }}>{chapter.details[0]}</div>
              )}
            </div>
          )}

          {!settled && slowHint && <div className="text-caption text-muted-foreground/70 italic mt-3">{slowHint}</div>}
        </>
      )}
    </div>
  );
}
